import styled from 'styled-components';
import {
  getProductCategory,
  ProductCategoryCode,
  ProductDTO,
} from 'src/models/pim';
import { ProductAttributeCode } from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Heading } from 'src/components/common/Heading';
import { media } from 'src/styles/designs';
import { ProductPageRelatedItem } from 'src/modules/products/components/ProductPage/ProductPageCrossSell/ProductPageRelatedItem';
import { SwiperSlide } from 'swiper/react';
import { ContentSwiper } from 'src/components/common/Swipers/ContentSwiper';
import { NavigationButton } from 'src/modules/materials/components/ShowcasePage/common';
import { head } from 'lodash';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';

const Container = styled.div`
  padding: 32px 0;
  overflow: hidden;
  background: #f6f6f6;

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 56px 0;
  }
`;

const SwiperContainer = styled(ContentSwiper)`
  justify-content: flex-start;

  &&& {
    ${NavigationButton} {
      top: 50%;
      transform: translateY(-50%);
      margin: 0;
      display: flex !important;
    }
  }

  .swiper-wrapper {
    gap: 20px;
  }
  .swiper-slide {
    width: min-content;
  }
`;

const categoryTitleMap: Record<ProductCategoryCode, DictionaryTitlesCode> = {
  [ProductCategoryCode.Cosmeceuticals]:
    DictionaryTitlesCode.CosmeceuticalsCrossSellingTitle,
  [ProductCategoryCode.Devices]: DictionaryTitlesCode.DevicesCrossSellingTitle,
  [ProductCategoryCode.Fillers]: DictionaryTitlesCode.FillersCrossSellingTitle,
};

export const ProductPageCrossSell = (product: ProductDTO) => {
  const translateFn = useTranslateItem();

  const { getProductAttribute } = useProductAttributes(product.attributes);

  const category = getProductCategory(product);

  const [dictionaryTitle] = useTitlesDictionary(categoryTitleMap[category]);

  const title =
    head(getProductAttribute(ProductAttributeCode.CrossSellingTitle)) ??
    dictionaryTitle;

  const items = product.cross_sell
    .map((p) => p.related_products_id)
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  return (
    <Container>
      <ConstraintContainer>
        {title && (
          <Heading>
            <Markdown>{translateFn(title)?.value}</Markdown>
          </Heading>
        )}

        <SwiperContainer>
          {items.map((item, index) => (
            <SwiperSlide key={item.code + index}>
              <ProductPageRelatedItem {...item} />
            </SwiperSlide>
          ))}
        </SwiperContainer>
      </ConstraintContainer>
    </Container>
  );
};
